import { useEffect, useRef } from "react";
import { getPersistSession } from "./api";

// Auto-log out after this many minutes of no user activity, ONLY when the
// user chose NOT to be remembered (i.e. session-only mode). "Remember me
// = on" users stay signed in until their JWT expires or they manually
// log out — matches the acceptance criteria for iter-39.
const IDLE_MINUTES = 60;
const IDLE_MS = IDLE_MINUTES * 60 * 1000;

// Events treated as "user is still around". Passive listeners so we don't
// interfere with scroll/touch performance. `visibilitychange` handles the
// case where the tab was backgrounded for a long time and then re-focused.
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "touchstart",
  "wheel",
  "scroll",
  "visibilitychange",
];

/**
 * useIdleLogout — client-side idle-timeout for the auth session.
 *
 *   • Enabled ONLY when the current session is session-only (Remember me
 *     is unchecked). Persistent sessions are exempt.
 *   • Resets the 60-minute countdown on any of ACTIVITY_EVENTS.
 *   • On timeout: calls `onIdle` (parent supplies `logout()` + a toast).
 *   • Cleans up all listeners + timers on unmount / auth-change / mode-flip.
 *
 * Exposes `window.__sfIdleLogout` in dev/preview with `{ trigger, setIdleMs }`
 * so E2E tests can force an immediate logout without waiting 60 minutes.
 */
export default function useIdleLogout({ isAuthenticated, onIdle }) {
  const timerRef = useRef(null);
  const onIdleRef = useRef(onIdle);
  // Keep the ref current without re-subscribing listeners each render.
  useEffect(() => { onIdleRef.current = onIdle; }, [onIdle]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Persistent-session users skip idle logout entirely.
    if (getPersistSession()) return;
    if (typeof window === "undefined") return;

    let currentMs = IDLE_MS;

    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    const schedule = () => {
      clear();
      timerRef.current = setTimeout(() => {
        // Guard: if the mode was flipped to persistent while the timer was
        // pending (e.g. via a tab that ticked the checkbox), don't nuke.
        if (getPersistSession()) return;
        try { onIdleRef.current && onIdleRef.current(); } catch { /* noop */ }
      }, currentMs);
    };
    const handleActivity = () => {
      // Ignore visibilitychange when the tab just went hidden — only reset
      // the timer when the user actually comes BACK to the tab.
      if (document.visibilityState === "hidden") return;
      schedule();
    };

    ACTIVITY_EVENTS.forEach((ev) => {
      const target = ev === "visibilitychange" ? document : window;
      target.addEventListener(ev, handleActivity, { passive: true });
    });
    schedule();

    // E2E hook — safe to expose since the only thing it can do is log the
    // current user out (same effect as walking away for an hour).
    window.__sfIdleLogout = {
      trigger: () => { try { onIdleRef.current && onIdleRef.current(); } catch { /* noop */ } },
      setIdleMs: (ms) => { currentMs = Math.max(500, Number(ms) || IDLE_MS); schedule(); },
      idleMs: () => currentMs,
      idleMinutesDefault: () => IDLE_MINUTES,
    };

    return () => {
      clear();
      ACTIVITY_EVENTS.forEach((ev) => {
        const target = ev === "visibilitychange" ? document : window;
        target.removeEventListener(ev, handleActivity);
      });
      try { delete window.__sfIdleLogout; } catch { window.__sfIdleLogout = undefined; }
    };
  }, [isAuthenticated]);
}
